/**
 * Same-track URI compare — web port of connector #570 `uri_same_track`.
 *
 * Exact match, same proxy session (`/stream/{id}/…`), or same vendor URL after
 * stripping only ephemeral query keys. Wholesale query-strip is forbidden —
 * Qobuz track identity lives in `track_id` / `trackid`.
 */

/** Query keys that rotate without changing track identity (tokens, signatures). */
const EPHEMERAL_QUERY_KEYS = new Set([
  'token',
  'exp',
  'expires',
  'expiry',
  'signature',
  'sig',
  'hmac',
  'auth',
  'authorization',
  'access_token',
]);

export function isEphemeralQueryKey(key: string): boolean {
  return EPHEMERAL_QUERY_KEYS.has(String(key || '').toLowerCase());
}

/** Proxy session id from `/stream/{id}/…` (token/exp path segments ignored). */
export function proxySessionId(uri: string): string | null {
  try {
    const parsed = new URL(uri);
    const parts = parsed.pathname.split('/').filter(Boolean);
    if (parts.length < 2) return null;
    if (parts[0].toLowerCase() !== 'stream') return null;
    const id = parts[1];
    return id ? id.toLowerCase() : null;
  } catch {
    return null;
  }
}

function normalizeStreamUri(uri: string): string {
  try {
    const parsed = new URL(uri);
    const pairs: Array<[string, string]> = [];
    parsed.searchParams.forEach((value, key) => {
      if (!isEphemeralQueryKey(key)) {
        pairs.push([key.toLowerCase(), value]);
      }
    });
    pairs.sort((a, b) => a[0].localeCompare(b[0]) || a[1].localeCompare(b[1]));
    let out = `${parsed.protocol.toLowerCase()}//`;
    if (parsed.hostname) out += parsed.hostname.toLowerCase();
    if (parsed.port) out += `:${parsed.port}`;
    out += parsed.pathname.replace(/\/+$/, '').toLowerCase();
    if (pairs.length) {
      out += `?${pairs.map(([k, v]) => `${k}=${v}`).join('&')}`;
    }
    return out;
  } catch {
    return uri.replace(/\/+$/, '').toLowerCase();
  }
}

/** True when two stream URIs identify the same track (#570 comparator). */
export function uriSameTrack(a: string, b: string): boolean {
  if (a === b) return true;
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  if (!left || !right) return false;
  const sa = proxySessionId(left);
  const sb = proxySessionId(right);
  if (sa && sb) return sa === sb;
  return normalizeStreamUri(left) === normalizeStreamUri(right);
}
